/**
 * New Relic ↔ Pendo bridge.
 *
 * Pushes Pendo visitor/account/session IDs into New Relic so every
 * subsequent PageAction, AjaxRequest, PageView, and JavaScriptError
 * event inherits them as custom attributes. This makes it possible to
 * join a Pendo session (guide view, feature click, etc.) to any
 * New Relic event via a single NRQL filter.
 *
 * Two entry points, both meant to be called together right after
 * `pendo.initialize(...)`:
 *
 * 1. `publishKnownPendoIdsToNewRelic({ bridgeId, commonOrgId })` — sync,
 *    zero-latency. Publishes the IDs we already have from Redux under
 *    the `*FromBridge` attribute names so events emitted before the
 *    Pendo SDK finishes bootstrapping still carry a correlation key.
 *    Uses DISTINCT attribute names from the SDK-authoritative values
 *    so a single NRQL join key is never polluted with two possible
 *    values (bridgeId vs. Pendo-normalized visitorId).
 *
 * 2. `publishPendoIdsFromSdkToNewRelic({ bridgeId, commonOrgId })` —
 *    polls `window.pendo` until `pendo.isReady()` returns true (or the
 *    timeout expires), then publishes the SDK's authoritative view
 *    under `pendoVisitorId` / `pendoAccountId` / `pendoSessionId` and
 *    emits a `PendoInitialized` PageAction whose payload includes
 *    `matched: visitorId === bridgeId` so we can detect (in NR) when
 *    Pendo normalized our ID. On failure emits `PendoInitFailed` with
 *    `reason: 'no_window_pendo' | 'timeout'`.
 *
 * Both are safe to call before `window.newrelic` or `window.pendo` are
 * ready — they no-op gracefully.
 */

type BridgeIds = {
    /** The Bridge (Okta) user id we passed to `pendo.initialize` as `visitor.id`. */
    bridgeId: string;
    /** The account/org id we passed to `pendo.initialize` as `account.id`. */
    commonOrgId?: string;
};

type PendoSdk = {
    isReady?: () => boolean;
    getVisitorId?: () => string | undefined;
    getAccountId?: () => string | undefined;
    getSessionId?: () => string | number | undefined;
};

type NewRelicApi = {
    setCustomAttribute?: (key: string, value: string | number | boolean | null) => void;
    addPageAction?: (name: string, attrs?: Record<string, unknown>) => void;
};

type PollOptions = {
    /** Interval between readiness checks, ms. Default 250. */
    pollMs?: number;
    /** Max number of readiness checks before giving up. Default 40 (~10s at 250ms). */
    maxAttempts?: number;
};

type FailureReason = 'no_window_pendo' | 'timeout';

// SDK-authoritative attributes — only set after `pendo.isReady()` returns true.
const NR_ATTR_VISITOR = 'customattr.coxauto:pendoVisitorId';
const NR_ATTR_ACCOUNT = 'customattr.coxauto:pendoAccountId';
const NR_ATTR_SESSION = 'customattr.coxauto:pendoSessionId';

// Pre-SDK attributes — set synchronously from Redux (bridgeId / commonOrgId).
// Distinct keys so early-session events have a correlation key without
// polluting the SDK-authoritative attribute name.
const NR_ATTR_VISITOR_BRIDGE = 'customattr.coxauto:pendoVisitorIdFromBridge';
const NR_ATTR_ACCOUNT_BRIDGE = 'customattr.coxauto:pendoAccountIdFromBridge';

const getNewRelic = (): NewRelicApi | undefined => {
    /* istanbul ignore next -- defensive guard for SSR; not exercised in jsdom tests */
    if (typeof window === 'undefined') return undefined;
    return (window as unknown as { newrelic?: NewRelicApi }).newrelic;
};

const getPendo = (): PendoSdk | undefined => {
    /* istanbul ignore next -- defensive guard for SSR; not exercised in jsdom tests */
    if (typeof window === 'undefined') return undefined;
    return (window as unknown as { pendo?: PendoSdk }).pendo;
};

const setAttr = (nr: NewRelicApi, key: string, value: string | undefined): void => {
    if (value === undefined || value === null || value === '') return;
    if (typeof nr.setCustomAttribute !== 'function') return;
    nr.setCustomAttribute(key, value);
};

/**
 * Safely invoke a Pendo SDK getter. `pendo.isReady()`, `pendo.getVisitorId()`,
 * `pendo.getAccountId()`, and `pendo.getSessionId()` are widely used in production
 * but are not listed in the officially-documented Pendo Web SDK API surface, so
 * Pendo reserves the right to change them between agent versions. This wrapper
 * ensures a future breaking change (removed method, thrown exception, wrong type)
 * degrades gracefully to `undefined` instead of crashing the poll loop.
 */
const safeCall = <T>(fn: (() => T) | undefined): T | undefined => {
    if (typeof fn !== 'function') return undefined;
    try {
        return fn();
    } catch {
        return undefined;
    }
};

const emitPendoInitFailed = (nr: NewRelicApi, reason: FailureReason, ids: BridgeIds, attempts: number): void => {
    if (typeof nr.addPageAction !== 'function') return;
    nr.addPageAction('PendoInitFailed', {
        reason,
        bridgeId: ids.bridgeId,
        commonOrgId: ids.commonOrgId,
        attempts
    });
};

/**
 * Synchronously publish the IDs we already have (from Redux) to New Relic
 * under distinct `*FromBridge` attribute keys, so events emitted before the
 * Pendo SDK finishes bootstrapping still carry a correlation key.
 *
 * Safe no-op if `window.newrelic` is not available.
 */
export const publishKnownPendoIdsToNewRelic = (ids: BridgeIds): void => {
    const nr = getNewRelic();
    if (!nr) return;
    setAttr(nr, NR_ATTR_VISITOR_BRIDGE, ids.bridgeId);
    setAttr(nr, NR_ATTR_ACCOUNT_BRIDGE, ids.commonOrgId);
};

/**
 * Poll `window.pendo` for readiness, then publish the SDK's authoritative
 * IDs (including `sessionId`) to New Relic. Emits a `PendoInitialized`
 * PageAction on success (payload includes `matched: visitorId === bridgeId`
 * so we can detect Pendo-side normalization drift in NRQL) or a
 * `PendoInitFailed` PageAction on failure (with `reason`).
 *
 * Safe no-op if `window.newrelic` is not available.
 */
export const publishPendoIdsFromSdkToNewRelic = (ids: BridgeIds, options: PollOptions = {}): void => {
    const nr = getNewRelic();
    if (!nr) return;

    const pollMs = options.pollMs ?? 250;
    const maxAttempts = options.maxAttempts ?? 40;

    let attempts = 0;

    const poll = (): void => {
        attempts++;
        const pendo = getPendo();

        if (pendo && safeCall(pendo.isReady)) {
            const visitorId = safeCall(pendo.getVisitorId);
            const accountId = safeCall(pendo.getAccountId);
            const rawSessionId = safeCall(pendo.getSessionId);
            const sessionId = rawSessionId === undefined || rawSessionId === null ? undefined : String(rawSessionId);

            setAttr(nr, NR_ATTR_VISITOR, visitorId);
            setAttr(nr, NR_ATTR_ACCOUNT, accountId);
            setAttr(nr, NR_ATTR_SESSION, sessionId);

            if (typeof nr.addPageAction === 'function') {
                nr.addPageAction('PendoInitialized', {
                    pendoVisitorId: visitorId,
                    pendoAccountId: accountId,
                    pendoSessionId: sessionId,
                    bridgeId: ids.bridgeId,
                    commonOrgId: ids.commonOrgId,
                    matched: visitorId === ids.bridgeId,
                    attempts
                });
            }
            return;
        }

        if (attempts < maxAttempts) {
            setTimeout(poll, pollMs);
            return;
        }

        // Poll exhausted — distinguish "Pendo script never loaded"
        // (ad blocker, CSP, network) from "loaded but never became ready".
        const reason: FailureReason = getPendo() === undefined ? 'no_window_pendo' : 'timeout';
        emitPendoInitFailed(nr, reason, ids, attempts);
    };

    poll();
};
